/* Real-snippet quick-add override v4 — renders the REAL Studio picker instances inside
 * the theme's existing mini-PDP dialog, replacing its JSON reconstruction.
 *
 * v4 invariants (each closes an audited defect):
 * - All swap/ATC state is keyed by product HANDLE and reset on every trigger click —
 *   a dialog can never show product B while carrying product A's variant or pickers.
 * - The observer re-swaps whenever the reconstruction is visible for the current
 *   product (not once per page load): reopen, same-product reopen, product switch
 *   inside an open dialog all converge to the real pickers.
 * - In-flight fetches that resolve after the user moved to another product are dropped.
 * - Header price/MRP/discount/thumb + ATC availability reprice from the product JSON
 *   on every spectrum:variant-change — the header can never disagree with what ATC adds.
 * - ATC refuses unavailable variants (Sold Out) and fails LOUD ("Try again") — never a
 *   silent no-op; the native path handles any dialog we didn't swap.
 * - Script loads are only marked done on onload; failures retry on the next open.
 * - ATC has a synchronous re-entry guard (data-sqa-busy): a fast double-click lands
 *   both clicks before the first await, so `disabled` alone cannot prevent dup adds. */
;(() => {
  if (window.__spectrum_qa_real__) return
  window.__spectrum_qa_real__ = true
  document.documentElement.classList.add('sqa-real-ready')

  // Variant pickers only — combos/size-guide/FBT are deliberately excluded.
  const SNIPPET_IDS = ['fszfrd01', 'fswfrd01', 'ftyfrd01']
  const CACHE_TTL_MS = 60000
  const CACHE_MAX = 40
  const cache = new Map() // handle -> {at, html, scripts, count}
  const jsonCache = new Map() // handle -> Promise<product|null>
  let lastHandle = null
  let lastVariantId = null
  let inflightHandle = null
  const noPicker = new Set() // handles with no real pickers → keep the theme reconstruction, never hide

  // Host layout: flex column so per-wrapper `order` pins visual order even if a
  // snippet re-render re-appends its wrapper (the order-flip bug).
  const st = document.createElement('style')
  st.textContent = '[data-spectrum-qa-options][data-real-snippet]{display:flex;flex-direction:column}'
    // Anti-flicker: while the real pickers resolve, hide the theme's reconstruction so the
    // swatches don't flash reconstruction → re-decorated reconstruction → real. A skeleton
    // spinner holds the space; :has() reveals the moment a real instance lands. Cleared in
    // replaceOptions (success / no-picker / error).
    + '[data-spectrum-qa-options][data-sqa-swapping]:not(:has([data-spectrum-instance-id])){min-height:96px;position:relative}'
    + '[data-spectrum-qa-options][data-sqa-swapping]:not(:has([data-spectrum-instance-id]))>*{visibility:hidden}'
    + '[data-spectrum-qa-options][data-sqa-swapping]:not(:has([data-spectrum-instance-id]))::after{content:"";position:absolute;top:50%;left:50%;width:22px;height:22px;margin:-11px 0 0 -11px;border:2px solid rgba(19,19,19,.18);border-top-color:#131313;border-radius:50%;animation:sqa-spin .6s linear infinite}'
    + '@keyframes sqa-spin{to{transform:rotate(360deg)}}'
  document.head.appendChild(st)

  document.addEventListener('cart:update', () => cache.clear()) // prices/availability may shift

  /* ---------- state tracking ---------- */
  document.addEventListener('click', (e) => {
    const t = e.target.closest && e.target.closest('[data-spectrum-quick-add-trigger]')
    if (t) {
      lastHandle = t.getAttribute('data-spectrum-quick-add-trigger') || null
      lastVariantId = null // per-open reset: a previous product's pick must never leak into ATC
    }
  }, true)

  window.addEventListener('spectrum:variant-change', (e) => {
    const vid = e.detail && e.detail.variantId
    if (!vid) return
    lastVariantId = vid
    repriceHeader(vid)
    // The picker JS writes ?variant= via replaceState — correct on a PDP, pollution on
    // a collection URL. It writes before dispatching, so strip it here.
    if (!location.pathname.includes('/products/') && new URLSearchParams(location.search).has('variant')) {
      const u = new URL(location.href)
      u.searchParams.delete('variant')
      history.replaceState(history.state, '', u.toString())
    }
  })

  const prefetch = (e) => {
    const t = e.target.closest && e.target.closest('[data-spectrum-quick-add-trigger]')
    if (!t) return
    if (navigator.connection && (navigator.connection.saveData || /(^|-)2g/.test(navigator.connection.effectiveType || ''))) return
    const h = t.getAttribute('data-spectrum-quick-add-trigger')
    if (h) fetchPickers(h).catch(() => {})
  }
  document.addEventListener('pointerenter', prefetch, true)
  document.addEventListener('touchstart', prefetch, { capture: true, passive: true })

  /* ---------- idle prefetch: warm the top cards WITHOUT touching collection-page perf ----------
     A cold quick-add open pays the full PDP fetch (~1s+ on a cache miss). Hover/touch prefetch
     (above) hides that on desktop; this warms the first few visible cards so a COLD tap (mobile,
     no dwell) also opens instantly. Kept strictly off the collection page's critical path:
     starts only after `load` + a settle delay, runs ONE fetch at a time inside
     requestIdleCallback (which yields to scroll/tap and runs only in genuine idle), is capped to
     a few cards, pauses while a dialog is open, and is skipped on saveData / slow connections. */
  ;(() => {
    const conn = navigator.connection || {}
    if (conn.saveData || /2g$/.test(conn.effectiveType || '')) return // skip data-saver + 2g/slow-2g
    const MAX = 4
    const ric = window.requestIdleCallback || ((fn) => setTimeout(() => fn(), 300))
    const run = () => {
      const handles = [...new Set([...document.querySelectorAll('[data-spectrum-quick-add-trigger]')]
        .filter((t) => { const r = t.getBoundingClientRect(); return r.bottom > 0 && r.top < window.innerHeight * 1.5 })
        .map((t) => t.getAttribute('data-spectrum-quick-add-trigger')).filter(Boolean))].slice(0, MAX)
      let i = 0
      const step = () => {
        if (i >= handles.length) return
        // never compete while the shopper is actively in the dialog
        if (document.querySelector('#spectrum-quick-add-dialog[open]')) { ric(step); return }
        fetchPickers(handles[i++]).catch(() => {}).finally(() => ric(step))
      }
      ric(step)
    }
    // after the page is fully loaded + settle so LCP/TBT are already measured; ric only fires in idle
    const arm = () => setTimeout(() => ric(run), 2500)
    if (document.readyState === 'complete') arm()
    else window.addEventListener('load', arm, { once: true })
  })()

  /* ---------- fetch + extract ---------- */
  async function fetchPickers(handle) {
    const hit = cache.get(handle)
    if (hit && Date.now() - hit.at < CACHE_TTL_MS) return hit
    cache.delete(handle)
    const res = await fetch('/products/' + encodeURIComponent(handle), { headers: { Accept: 'text/html' } })
    if (!res.ok) throw new Error('pdp fetch ' + res.status)
    const doc = new DOMParser().parseFromString(await res.text(), 'text/html')
    // PDP document order is the source of truth for picker order (Color above Size iff
    // the PDP renders it that way). One instance per snippet type: keep the first-seen
    // position, prefer a vis="on" copy for content.
    const sel = SNIPPET_IDS.map((id) => `[data-spectrum-instance-id][data-spectrum-snippet-id="${id}"]`).join(',')
    const picked = new Map() // snippetId -> { idx, node }
    ;[...doc.querySelectorAll(sel)].forEach((n, idx) => {
      const id = n.getAttribute('data-spectrum-snippet-id')
      const cur = picked.get(id)
      if (!cur) picked.set(id, { idx, node: n })
      else if (cur.node.getAttribute('data-spectrum-vis') !== 'on' && n.getAttribute('data-spectrum-vis') === 'on') cur.node = n
    })
    const holder = document.createElement('div')
    ;[...picked.values()].sort((a, b) => a.idx - b.idx).forEach(({ node }, i) => {
      const c = node.cloneNode(true)
      c.setAttribute('data-spectrum-vis', 'on')
      c.querySelectorAll('[data-spectrum-vis]').forEach((x) => x.setAttribute('data-spectrum-vis', 'on'))
      // Pin visual order with flex `order` — survives any snippet re-render that
      // re-appends its wrapper to the container end (the observed order-flip bug).
      c.style.order = String(i)
      holder.appendChild(c)
    })
    const scripts = [...new Set([...doc.querySelectorAll('script[src]')].map((s) => s.getAttribute('src'))
      .filter((s) => s && /sai-snippet-[a-z0-9]+[^"']*\.js/.test(s)))]
    const entry = { at: Date.now(), html: holder.innerHTML, scripts, count: holder.children.length }
    if (cache.size >= CACHE_MAX) cache.delete(cache.keys().next().value)
    cache.set(handle, entry)
    return entry
  }

  const loaded = new Set([...document.querySelectorAll('script[src]')].map((s) => (s.getAttribute('src') || '').split('?')[0]))
  function ensureScripts(srcs) {
    return Promise.all(srcs.map((src) => {
      const k = src.split('?')[0]
      if (loaded.has(k)) return Promise.resolve()
      return new Promise((r) => {
        const el = document.createElement('script')
        el.src = src
        el.onload = () => { loaded.add(k); r() } // only mark loaded on success
        el.onerror = () => { console.error('[spectrum-qa-real] picker script failed', src); r() } // next open retries
        document.head.appendChild(el)
      })
    }))
  }

  function productJson(handle) {
    if (!jsonCache.has(handle)) {
      jsonCache.set(handle, fetch('/products/' + encodeURIComponent(handle) + '.js', { headers: { Accept: 'application/json' } })
        .then((r) => (r.ok ? r.json() : null)).catch(() => null))
    }
    return jsonCache.get(handle)
  }

  /* ---------- header reprice (single source of truth: variant-change events) ---------- */
  const fmt = (paise) => '₹' + Math.round(paise / 100).toLocaleString('en-IN')
  async function repriceHeader(variantId) {
    const handle = lastHandle
    if (!handle) return
    const opts = document.querySelector('[data-spectrum-qa-options]')
    if (!opts || opts.dataset.realSnippet !== handle) return // only reprice dialogs we own
    const p = await productJson(handle)
    if (!p || handle !== lastHandle) return
    const v = (p.variants || []).find((x) => String(x.id) === String(variantId))
    if (!v) return
    document.querySelectorAll('[data-spectrum-qa-prices]').forEach((el) => {
      const cmp = v.compare_at_price && v.compare_at_price > v.price
        ? ` <s style="color:#9aa0a6;font-weight:400">${fmt(v.compare_at_price)}</s>` : ''
      const pct = v.compare_at_price && v.compare_at_price > v.price
        ? Math.round(((v.compare_at_price - v.price) / v.compare_at_price) * 100) : 0
      const pill = pct > 0
        ? ` <span style="background:#e6f4ea;color:#1e7e34;border-radius:999px;padding:2px 8px;font-size:12px;font-weight:600">${pct}% OFF</span>` : ''
      el.innerHTML = `<strong>${fmt(v.price)}</strong>${cmp}${pill}`
    })
    const img = v.featured_image && (v.featured_image.src || (typeof v.featured_image === 'string' ? v.featured_image : null))
    if (img) { const t = document.querySelector('[data-spectrum-qa-thumb] img'); if (t) t.src = img }
    const atc = document.querySelector('[data-spectrum-qa-atc]')
    if (atc) { atc.disabled = !v.available; atc.textContent = v.available ? 'Add to Cart' : 'Sold Out' }
  }

  /* ---------- swap ---------- */
  async function replaceOptions(handle) {
    if (inflightHandle === handle) return
    inflightHandle = handle
    try {
      const entry = await fetchPickers(handle)
      if (handle !== lastHandle) return // user moved on — drop stale content
      const opts = document.querySelector('[data-spectrum-qa-options]')
      if (!opts) return
      if (!entry.count) { noPicker.add(handle); opts.removeAttribute('data-sqa-swapping'); opts.dataset.realSnippet = ''; return } // no Spectrum pickers → keep reconstruction + native ATC
      await ensureScripts(entry.scripts)
      if (handle !== lastHandle) return
      opts.innerHTML = entry.html
      opts.dataset.realSnippet = handle // ATC hijack + reprice are gated on this matching lastHandle
      opts.removeAttribute('data-sqa-swapping') // real pickers injected — reveal (once)
      const rb = window.__saiRebind || {}
      Object.values(rb).forEach((fn) => { try { fn() } catch {} })
      // Bind watchdog: snippet JS binds via its MutationObserver or the __saiRebind hook.
      // If neither bound the wrappers (observer past its window, hook absent), showing
      // dead pickers is worse than the reconstruction — revert LOUDLY to the native UI.
      setTimeout(() => {
        if (opts.dataset.realSnippet !== handle || handle !== lastHandle) return
        const bound = [...opts.querySelectorAll('*')].some((n) => n._wrapBound === true)
        if (bound) return
        console.error('[spectrum-qa-real] pickers did not bind — reverting to native picker UI')
        noPicker.add(handle)
        opts.removeAttribute('data-sqa-swapping')
        opts.dataset.realSnippet = ''
        try { const qa = window.spectrumQuickAdd; if (qa && typeof qa.renderOptions === 'function') qa.renderOptions() } catch {}
      }, 900)
    } catch (err) {
      console.error('[spectrum-qa-real] swap failed — native dialog remains', err)
      const opts = document.querySelector('[data-spectrum-qa-options]')
      if (opts) { opts.removeAttribute('data-sqa-swapping'); if (!opts.dataset.realSnippet) opts.dataset.realSnippet = '' } // release skeleton; reconstruction shows
    } finally {
      if (inflightHandle === handle) inflightHandle = null
    }
  }

  // Self-healing: whenever the dialog shows the reconstruction (children but no real
  // wrapper) for the current product, swap. Covers first open, reopen, product switch.
  const mo = new MutationObserver(() => {
    const opts = document.querySelector('[data-spectrum-qa-options]')
    if (!opts || !lastHandle) return
    if (opts.children.length && !opts.querySelector('[data-spectrum-instance-id]')) {
      if (!noPicker.has(lastHandle)) opts.setAttribute('data-sqa-swapping', lastHandle) // hide reconstruction until real pickers land
      replaceOptions(lastHandle)
    }
  })
  mo.observe(document.documentElement, { childList: true, subtree: true })

  /* ---------- ATC ---------- */
  document.addEventListener('click', (e) => {
    const atc = e.target.closest && e.target.closest('[data-spectrum-qa-atc]')
    if (!atc) return
    const opts = document.querySelector('[data-spectrum-qa-options]')
    // Hijack ONLY when the CURRENT product's real pickers are displayed; otherwise the
    // native handler (which is correct for its own reconstruction) runs untouched.
    if (!opts || !lastHandle || opts.dataset.realSnippet !== lastHandle) return
    e.stopImmediatePropagation()
    e.preventDefault()
    // Synchronous re-entry guard: two clicks in the same task both reach this
    // handler before any await runs (`disabled` is only set later), so without
    // this flag a fast double-click double-adds. Cleared in the IIFE's finally.
    if (atc.disabled || atc.dataset.sqaBusy === '1') return
    const id = lastVariantId
    if (!id) { atc.textContent = 'Select options'; return }
    atc.dataset.sqaBusy = '1'
    ;(async () => {
      const qa = window.spectrumQuickAdd
      window.__spectrumQuickAddInFlight = true
      try {
        const p = await productJson(lastHandle)
        const v = p && (p.variants || []).find((x) => String(x.id) === String(id))
        if (v && v.available === false) { atc.disabled = true; atc.textContent = 'Sold Out'; return }
        atc.disabled = true
        atc.textContent = 'Adding…'
        if (qa && typeof qa.addToCart === 'function' && typeof qa.dispatchCartUpdate === 'function') {
          const { cart, sections } = await qa.addToCart(Number(id))
          qa.dispatchCartUpdate(cart, 'spectrum-quick-add', { variantId: String(id), sections })
          if (typeof qa.close === 'function') qa.close()
          else { const c = document.querySelector('[data-spectrum-qa-close]'); if (c) c.click() }
        } else {
          const res = await fetch('/cart/add.js', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ id: Number(id), quantity: 1 }) })
          if (!res.ok) throw new Error('cart add ' + res.status)
          const cart = await fetch('/cart.js', { headers: { Accept: 'application/json' } }).then((r) => r.json()).catch(() => ({}))
          const evt = new Event('cart:update', { bubbles: true })
          evt.detail = { resource: cart || {}, sourceId: 'spectrum-qa-real', data: { source: 'spectrum-qa-real', variantId: String(id) } }
          document.dispatchEvent(evt)
          const c = document.querySelector('[data-spectrum-qa-close]'); if (c) c.click()
        }
        atc.disabled = false
        atc.textContent = 'Add to Cart'
      } catch (err) {
        console.error('[spectrum-qa-real] ATC failed', err)
        atc.disabled = false
        atc.textContent = 'Try again'
      } finally {
        delete atc.dataset.sqaBusy
        window.__spectrumQuickAddInFlight = false
      }
    })()
  }, true)
})()
